import React, { lazy, Suspense, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client';

import { PRODUCT_FRAGMENT } from '../../../component/productPreview/ProductPreview';
import ProductsList from '../../../component/productsList/ProductsList';
import { PRODUCT_LIST_ITEM_FRAGMENT } from '../../../query/productFragments';

// Search filter & sort integrations
import { useFilterStore } from '../../search/searchFilterPanel/FilterStoreProvider';
import useProductSearchVariables from '../../search/searchProductContainer/hooks/useProductSearchVariables';
import SortDropdown from '../../search/searchSort/SortDropdown';
import useSortStore from '../../search/searchSort/SortStore';
import { SORT_OPTIONS as SORT } from '../../search/searchSort/SortOptions';
import useFilterSortUrlSync from '../../../hook/useFilterSortUrlSync';
import FilterTags from '../../search/searchFilterTags/FilterTags';
import { getAllCategories } from '../../search/searchUtils';

import '../../search/Search.css';
import useMediaQuery from '../../../hook/useMediaQuery';
import MountedWrapper from '../../../component/mountedWrapper/MountedWrapper';
import IconFilter from '../../../assets/icon_slider.svg';

const FilterPanel = lazy(() => import('../../search/searchFilterPanel/FilterPanel'));
const MobileFilterPanel = lazy(() => import('../../search/searchFilterPanel/MobileFilterPanel'));

// GraphQL queries
const GET_CATEGORIES_FOR_FILTER = gql`
    query getCategoriesCached {
        getCategoriesCached(limit: 100) {
            name
            subcategories {
                id
                name
            }
        }
    }
`;
const SEE_SELLER_PRODUCTS = gql`
    query SeeSellerProducts($filter: ProductFilter!, $offset: Int, $limit: Int, $sort: String) {
        searchProducts(filter: $filter, offset: $offset, limit: $limit, sort: $sort, origin: "SELLER") {
            ...ProductFragment
            ...ProductListItemFragment
            price
        }
    }
    ${PRODUCT_FRAGMENT}
    ${PRODUCT_LIST_ITEM_FRAGMENT}
`;

function ShopContent({ seller, keyword }) {
    const filters = useFilterStore((state) => state.filters);
    const updateFilter = useFilterStore((state) => state.updateFilter);
    const clearAllFilters = useFilterStore((state) => state.clearAllFilters);
    const selectedSort = useSortStore((state) => state.selectedSort);
    const setSort = useSortStore((state) => state.setSort);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 767px)');
    const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
    const isSmall = isMobile || isTablet;

    // URL <-> 상태 공용 동기화 훅 (셀러 기본 정렬: 최신순)
    useFilterSortUrlSync('NEW');

    // 카테고리 데이터 (FilterTags용)
    const { data: catData } = useQuery(GET_CATEGORIES_FOR_FILTER);
    const allCategories = getAllCategories(catData);

    // FilterTags 핸들러
    const handleRemoveFilter = (filterType, value) => {
        switch (filterType) {
            case 'gender':
                updateFilter('gender', '');
                break;
            case 'subcategoryIds':
                updateFilter('subcategoryIds', filters.subcategoryIds.filter((id) => id !== value));
                break;
            case 'brands':
                updateFilter(
                    'brands',
                    (filters.brands || []).filter((brand) => {
                        const brandName = typeof brand === 'string' ? brand : brand.name;
                        const valueName = typeof value === 'string' ? value : value.name;
                        return brandName !== valueName;
                    })
                );
                break;
            case 'price':
                updateFilter('priceMin', null);
                updateFilter('priceMax', null);
                break;
            case 'isSelling':
                updateFilter('isSelling', false);
                break;
            case 'condition':
                updateFilter('condition', '');
                break;
            case 'sizeValueIds': {
                const removeIds = new Set((value?.valueIds || []).map(Number));
                const remaining = (filters.sizeValueIds || []).filter(
                    (id) => !removeIds.has(Number(id))
                );
                updateFilter('sizeValueIds', remaining);
                break;
            }
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        clearAllFilters();
        // 셀러샵에서는 기본 정렬을 최신순으로 고정
        setSort(SORT.NEW.code);
    };

    const baseVariables = useProductSearchVariables(keyword, filters, selectedSort);
    const variables = useMemo(
        () => ({
            ...baseVariables,
            filter: {
                ...baseVariables.filter,
                sellerId: parseInt(seller.id, 10),
            },
        }),
        [baseVariables, seller.id]
    );

    // 정렬 필터 제약은 SortDropdown 내부(origin 기반)에서만 처리

    return (
        <>
            <div
                className="Search-content"
                style={{ maxWidth: 'none', padding: 0, display: isSmall ? 'block' : undefined }}
            >
                {!isSmall && (
                    <MountedWrapper>
                        <div className="Search-sidebar">
                            <Suspense fallback={null}>
                                <FilterPanel />
                            </Suspense>
                        </div>
                    </MountedWrapper>
                )}
                <div className={`Search-results ${isSmall ? 'Search-results-full' : ''}`}>
                    {isSmall && (
                        <div className="Search-results-header">
                            <button className="Search-filter-button" onClick={() => setIsFilterOpen(true)}>
                                <img src={IconFilter} alt="filter" />
                                필터
                            </button>
                            <div className="Search-sort-container">
                                <SortDropdown
                                    selectedSort={selectedSort}
                                    onSortChange={setSort}
                                    origin="SELLER"
                                />
                            </div>
                        </div>
                    )}
                    {!isSmall && (
                        <>
                            <FilterTags
                                filters={filters}
                                onRemoveFilter={handleRemoveFilter}
                                onClearAll={handleClearAllFilters}
                                categories={allCategories}
                            />
                            <div className="Search-results-header">
                                <div className="Search-sort-container">
                                    <SortDropdown
                                        selectedSort={selectedSort}
                                        onSortChange={setSort}
                                        origin="SELLER"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <ProductsList
                        itemsProperty="searchProducts"
                        query={SEE_SELLER_PRODUCTS}
                        variables={variables}
                        eagerImageCount={4}
                        prioritizeFirstImage={!seller.user?.resizedBigCoverImage}
                    />
                </div>
            </div>
            {isSmall && isFilterOpen && (
                <MountedWrapper>
                    <Suspense fallback={null}>
                        <MobileFilterPanel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} />
                    </Suspense>
                </MountedWrapper>
            )}
        </>
    );
}

ShopContent.propTypes = {
    seller: PropTypes.shape({
        id: PropTypes.string.isRequired,
        user: PropTypes.shape({
            resizedBigCoverImage: PropTypes.string,
        }),
    }).isRequired,
    keyword: PropTypes.string,
};

export default ShopContent;
